import React, { useState, useEffect } from "react";
import { Grid, Button, Icon, Loader, Message, Checkbox, MessageHeader, Popup } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
// import { useMediaQuery } from "react-responsive";
import { onSnapshot } from "firebase/firestore";
import {
  fetchComplianceChecklistTemplateFromDb,
  fetchComplianceChecklistTemplateByBrokerageFromDb,
  fetchComplianceChecklistTemplateByManagerFromDb,
  fetchBrokerageFormsFromDb,
  // updateComplianceItemStatusInDb,
} from "../../app/firestore/firestoreService";
import { fetchComplianceTemplate, setError } from "./complianceSlice";
import { openModal } from "../../app/common/modals/modalSlice";
// import { toast } from "react-toastify";
import ComplianceChecklistTable from "./complianceComponents/ComplianceChecklistTable";

export default function ComplianceDashboard() {
  const dispatch = useDispatch();
  const { transaction } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { docsTransActive, docsTransArchived, docsTransShared } = useSelector((state) => state.doc);
  const { complianceTemplate } = useSelector((state) => state.compliance);
  const [loading, setLoadingState] = useState(true);
  const [showConditional, setShowConditional] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showFolders, setShowFolders] = useState(false);
  const [brokerageForms, setBrokerageForms] = useState([]);
  // const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const transactionId = transaction?.id;
  const brokerageId = transaction?.agentProfile?.brokerageId;
  const brokerageName = transaction?.agentProfile?.brokerageForms; // brokerage identifier for forms
  const stateProvince = transaction?.agentProfile?.state || "Colorado";
  // const agentProfile = transaction?.agentProfile;
  const agentRepresents = transaction?.agentRepresents || "Buyer";
  const managerId = transaction?.managerId;

  useEffect(() => {
    const complianceChecklistOptions =
      currentUserProfile?.complianceChecklistOptions || {};

    setShowArchived(!!complianceChecklistOptions.includeArchivedDocuments);
    setShowFolders(!!complianceChecklistOptions.includeFolders);
    setShowConditional(!!complianceChecklistOptions.showConditional);
    setShowOptional(!!complianceChecklistOptions.showOptional);
  }, [currentUserProfile?.complianceChecklistOptions]);

  const folderDocs = (docsTransArchived?.docs || []).filter(
    (doc) => doc.isFolder === true
  );
  const nonFolderArchivedDocs = (docsTransArchived?.docs || []).filter(
    (doc) => doc.isFolder !== true
  );

  useEffect(() => {
    if (!transactionId) return;

    setLoadingState(true);

    // Resolution chain: manager template -> brokerage template -> state default.
    const queryChain = [];
    if (managerId) {
      queryChain.push(() =>
        fetchComplianceChecklistTemplateByManagerFromDb(
          stateProvince,
          managerId,
          agentRepresents
        )
      );
    }
    if (brokerageId) {
      queryChain.push(() =>
        fetchComplianceChecklistTemplateByBrokerageFromDb(
          stateProvince,
          brokerageId,
          agentRepresents
        )
      );
    }
    queryChain.push(() =>
      fetchComplianceChecklistTemplateFromDb(stateProvince, agentRepresents)
    );

    const unsubscribes = [];

    const trySubscribe = (index) => {
      if (index >= queryChain.length) {
        setLoadingState(false);
        return;
      }
      try {
        const unsubscribe = onSnapshot(
          queryChain[index](),
          (snapshot) => {
            if (!snapshot.empty) {
              const templateData = {
                id: snapshot.docs[0].id,
                ...snapshot.docs[0].data(),
              };
              dispatch(fetchComplianceTemplate(templateData));
              setLoadingState(false);
            } else {
              trySubscribe(index + 1);
            }
          },
          (error) => {
            console.error("Error fetching compliance template:", error);
            dispatch(setError(error.message));
            setLoadingState(false);
          }
        );
        unsubscribes.push(unsubscribe);
      } catch (error) {
        console.error("Error setting up compliance template listener:", error);
        dispatch(setError(error.message));
        setLoadingState(false);
      }
    };

    trySubscribe(0);

    return () => {
      unsubscribes.forEach((u) => u && u());
    };
  }, [transactionId, stateProvince, brokerageId, managerId, agentRepresents, dispatch]);

  // Fetch brokerage forms for the agent's brokerage
  useEffect(() => {
    if (!stateProvince || !brokerageName) {
      setBrokerageForms([]);
      return;
    }

    const brokerageFormsQuery = fetchBrokerageFormsFromDb(stateProvince, brokerageName);
    const unsubscribe = onSnapshot(
      brokerageFormsQuery,
      (snapshot) => {
        const forms = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBrokerageForms(forms);
      },
      (error) => {
        console.error("Error fetching brokerage forms:", error);
        setBrokerageForms([]);
      }
    );

    return () => unsubscribe();
  }, [stateProvince, brokerageName]);

  function handleNotify() {
    dispatch(
      openModal({
        modalType: "ComplianceNotificationModal",
        modalProps: {
          transaction,
          template: complianceTemplate,
          documents: [
                ...(docsTransActive?.docs || []),
                ...(showArchived ? nonFolderArchivedDocs : []),
                ...(showFolders ? folderDocs : []),
                ...(docsTransShared?.docs || []),
              ].filter(doc => doc.signingComplete === true), //docsTransActive?.docs || [],
          brokerageForms,
        },
      })
    );
  }

  if (loading) {
    return (
      <div className="secondary-page-wrapper">
        <Loader active>Loading compliance checklist...</Loader>
      </div>
    );
  }

  if (!complianceTemplate) {
    return (
      <div className="secondary-page-wrapper">
        <Message warning>
          <Message.Header>No Compliance Template Found</Message.Header>
          <p>
            No compliance checklist template is configured for{" "}
            {transaction?.agentProfile?.state || "this state"}. Please contact your
            administrator.
          </p>
        </Message>
      </div>
    );
  }

  console.log("ComplianceDashboard: complianceTemplate: ", complianceTemplate);
  console.log("docsTransActive: ", docsTransActive?.docs);

  return (
    <div className="secondary-page-wrapper">
      <Grid stackable>
        <Grid.Row>
          <Grid.Column computer={8}>
             {false && (
                    <div className="text-center">
                      <Message color="blue" className="zero margin">
                        <MessageHeader>
                          The Compliance feature is in beta testing.
                          <br/>
                          Please send your suggestions and feedback to support@transactioner.com
                        
                        </MessageHeader>
                      </Message>
                    </div>
                  )}
            <h2 className="zero bottom margin">Compliance Checklist</h2>
           
          </Grid.Column>
          <Grid.Column computer={8}>
            <Button
              primary
              size="small"
              onClick={handleNotify}
              icon
              labelPosition="left"
              floated="right"
            >
              <Icon name="mail" />
              Notify Team
            </Button>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column computer={16}>
            <p className="small top margin text-grey">
              Track required documents and disclosures marked as Signing Complete for this transaction.
              <Popup
                        flowing
                        size="small"
                        trigger={
                          <Icon
                            name="info"
                            color="blue"
                            circular
                            inverted
                            size="tiny"
                            style={{ marginLeft: "3px", marginBottom: "3px" }}
                          />
                        }
                      >
                        <p className="bold text blue mini bottom margin">
                          Compliance Checklist
                        </p>

                        <p className="text-dark text-normal mini bottom margin">
                            <b>Compliance Documents</b>
                            <br/>
                            Only documents marked as "Signing Complete" show up in this Compliance Checklist.
                            <br/>
                            To mark a document as signing complete, 
                            <br />go to the Documents page and click the drop-down on a document and select "Mark Signing Complete".
                          </p>
                        
                          <p className="text-dark text-normal mini bottom margin">
                            <b>Checkbox Filters</b>
                            <br/>
                            You can filter using the checkboxes to include folders and archived documents, 
                            <br />
                            or show compliance requirements that are designated as conditional or optional. 
                            <br />
                            <i>These filters can default to be selected on all of your Compliance Checklist pages 
                            <br/>
                            by going to the "Compliance" section of your profile page 
                            <br />(click on your name in the upper-right and select 'My Profile').</i>
                          </p>
                        
                      </Popup>
            </p>
          </Grid.Column>
        </Grid.Row>

        <Grid.Row>
          <Grid.Column computer={4}>
            <p className="small top margin text-grey">
              <strong>Transaction Status: </strong> {transaction?.status}
            </p>
          </Grid.Column>
          <Grid.Column computer={2}>
            <p className="small top margin text-grey">
              <strong>Year Built: </strong> {transaction?.propertyDetails?.yearBuilt || ""}
            </p>
          </Grid.Column>
          
          <Grid.Column computer={10} textAlign="right">

            <Checkbox
              label="Include Folders"
              checked={showFolders}
              onChange={(e, { checked }) => setShowFolders(checked)}
              style={{ marginRight: "20px" }}
            />
            <Checkbox
              label="Include Archived Documents"
              checked={showArchived}
              onChange={(e, { checked }) => setShowArchived(checked)}
              style={{ marginRight: "20px" }}
            />
            <Checkbox
              label="Show Conditional"
              checked={showConditional}
              onChange={(e, { checked }) => setShowConditional(checked)}
              style={{ marginRight: "20px" }}
            />
            <Checkbox
              label="Show Optional"
              checked={showOptional}
              onChange={(e, { checked }) => setShowOptional(checked)}
            />
          </Grid.Column>
        </Grid.Row>
       

        <Grid.Row>
          <Grid.Column computer={16}>
            <ComplianceChecklistTable
              template={complianceTemplate}
              transaction={transaction}
              documents={[
                ...(docsTransActive?.docs || []),
                ...(showArchived ? nonFolderArchivedDocs : []),
                ...(showFolders ? folderDocs : []),
                ...(docsTransShared?.docs || []),
              ].filter(doc => doc.signingComplete === true)}
              showConditional={showConditional}
              showOptional={showOptional}
              brokerageForms={brokerageForms}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
}

